import { useCallback, useEffect, useMemo, useState } from 'react';
import useUserQuery from '../../hooks/queries/useUserQuery';
import { useGetBettieProducts } from '../../../domains/bettie/ui/hooks/useGetBettieProducts';
import { openNewTab } from '../../../utils/UIUtils';
import type { components } from '../../../types/schema';

type ApiV1BettieProduct = components['schemas']['ApiV1BettieProduct'];

export const useBettieCharge = (onClose?: () => void) => {
  const { data: oneTimeProducts, isFetching: isBettieProductsLoading } = useGetBettieProducts({
    category: 'onetime',
    assetType: 'bettie_point'
  });
  
  const { data: subscriptionProducts, isFetching: isSubscriptionProductsLoading } =
    useGetBettieProducts({
      category: 'subscription',
      assetType: 'bettie_point'
    });

  const { data: user, refetch: refetchUser } = useUserQuery();

  // 모든 상품을 하나의 배열로 합치기 (subscriptionProducts 우선순위)
  const allProducts = useMemo(
    () => [...(subscriptionProducts || []), ...(oneTimeProducts || [])],
    [subscriptionProducts, oneTimeProducts]
  );

  const [selectedItem, setSelectedItem] = useState<ApiV1BettieProduct | null>(null);
  const [isItemListOpen, setIsItemListOpen] = useState(false);
  // 원래 선택된 상품을 저장
  const [originalSelectedItem, setOriginalSelectedItem] = useState<ApiV1BettieProduct | null>(null);

  useEffect(() => {
    if (allProducts.length > 0) {
      setSelectedItem(allProducts[0]);
      setOriginalSelectedItem(allProducts[0]);
    } else {
      setSelectedItem(null);
      setOriginalSelectedItem(null);
    }
  }, [allProducts]);

  const handleSelectItem = useCallback(
    (id: string) => {
      const item = allProducts.find((item) => item.bettieProductId === id) ?? null;
      setSelectedItem(item);
      setOriginalSelectedItem(item);
    },
    [allProducts]
  );

  const handleSelectBundle = useCallback(
    (bundle: ApiV1BettieProduct | null) => {
      if (bundle) {
        setSelectedItem(bundle);
      } else {
        // bundle 선택 해제 시 원래 상품으로 복원
        setSelectedItem(originalSelectedItem);
      }
    },
    [originalSelectedItem]
  );

  useEffect(() => {
    refetchUser();
    const onFocus = () => {
      refetchUser();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refetchUser]);

  const handlePrimaryClick = () => {
    if (!selectedItem || !user) return;

    const amount = Number(selectedItem.baseAmount - selectedItem.discountAmount);

    if (selectedItem.category === 'subscription' || selectedItem.category === 'bundle') {
      if (user?.identified) {
        openNewTab(
          `/payment/billing?amount=${amount}&productName=${selectedItem.name}&productId=${selectedItem.bettieProductId}`
        );
      } else {
        openNewTab(
          `/identity-verification/intro?amount=${amount}&productName=${selectedItem.name}&productId=${selectedItem.bettieProductId}&isBilling=true`
        );
      }
      onClose?.();
      return;
    }

    if (user?.identified) {
      openNewTab(
        `/payment/inicis?amount=${amount}&productName=${selectedItem.name}&productId=${selectedItem.bettieProductId}`
      );
    } else {
      openNewTab(
        `/identity-verification/intro?amount=${amount}&productName=${selectedItem.name}&productId=${selectedItem.bettieProductId}`
      );
    }
    onClose?.();
  };

  return {
    products: allProducts,
    selectedItem,
    originalSelectedItem,
    isItemListOpen,
    setIsItemListOpen,
    handleSelectItem,
    handleSelectBundle,
    handlePrimaryClick,
    isLoadingProducts: isBettieProductsLoading || isSubscriptionProductsLoading
  };
};

export default useBettieCharge;
